import { observer } from 'mobx-react-lite';
import { ReactElement, ReactNode, useEffect, useRef } from 'react';
import Query from '../../store/utils/query/Query';
import { DisabledContainer } from '../display';
import ErrorBox from '../display/ErrorBox';
import { Pagination } from './index';
import LoadingBox from './LoadingBox';
import NoItemsBox from './NoItemsBox';

export default observer(function QueryContainer<T, TResult>({
  children,
  empty = () => <NoItemsBox itemsName={itemsName} sx={{ mt: 2 }} />,
  itemsName,
  noPagination = false,
  pending,
  query,
  rejected = (e) => <ErrorBox error={e} />,
}: {
  children: ((value: TResult) => ReactElement) | ReactElement;
  empty?: ((value?: TResult | void) => ReactElement) | false;
  itemsName?: ReactNode;
  noPagination?: boolean;
  pending?: () => ReactElement;
  query: Query<T, TResult>;
  rejected?: (error: Error) => ReactElement;
}) {
  const prevValue = usePrevious(query?.promise?.value);

  if (!query?.promise) {
    return null;
  }

  const { state, value } = query.promise;
  const anyValue = value as any;

  switch (state) {
    case 'fulfilled': {
      const isEmpty =
        !value ||
        (Array.isArray(value) && value.length === 0) ||
        anyValue?.isEmpty;

      if (isEmpty && typeof empty === 'function') {
        return empty(anyValue);
      }

      return (
        <RenderContent
          noPagination={noPagination}
          query={query as any}
          value={anyValue}
        >
          {children}
        </RenderContent>
      );
    }
    case 'pending': {
      if (pending) {
        return pending();
      }

      if (prevValue) {
        return (
          <RenderContent
            isPending
            noPagination={noPagination}
            query={query as any}
            value={prevValue}
          >
            {children}
          </RenderContent>
        );
      }

      return (
        <LoadingBox>
          {itemsName && <span>Loading {itemsName}…</span>}
        </LoadingBox>
      );
    }
    case 'rejected': {
      return rejected(anyValue);
    }
    default: {
      return rejected(new Error(`Unknown Resource state ${state}`));
    }
  }
});

function RenderContent({
  children,
  isPending = false,
  noPagination,
  query,
  value,
}: {
  children: ((value: any) => ReactElement) | ReactElement;
  isPending?: boolean;
  noPagination: boolean;
  query: Query<any>;
  value: any;
}) {
  return (
    <DisabledContainer isDisabled={isPending}>
      {typeof children === 'function' ? children(value) : children}
      {!noPagination && <Pagination query={query} />}
    </DisabledContainer>
  );
}

function usePrevious(value: any) {
  const ref = useRef();
  useEffect(() => {
    if (value) {
      ref.current = value;
    }
  }, [value]);
  return ref.current;
}
